// @flow
import * as React from "react";
import PageHeading from "../../components/PageHeading";
import { Form, Formik } from "formik";
import { LinearProgress } from "@material-ui/core";
import { useSnackbar } from "notistack";
import Button from "@material-ui/core/Button";
import {
  useFirestore,
  useFirestoreCollectionData,
  useFirestoreDocDataOnce,
} from "reactfire";
import type { Game, TicketReservation } from "../../common/types";
import ListItem from "@material-ui/core/ListItem";
import List from "@material-ui/core/List";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";

type Props = {
  game: Game,
  ticketReservation: TicketReservation,
};

function GameAdminSmartCheckInFormValidate({
  game,
  gameRef,
  setStep,
  handleClose,
  ticketReservation,
  keepOpen,
  setKeepOpen,
}: Props) {
  const firestore = useFirestore();
  const userDataRef = firestore
    .collection("users")
    .doc(ticketReservation.userId);
  const userData = useFirestoreDocDataOnce(userDataRef);
  const companionsRef = userDataRef.collection("companions");
  const companions = useFirestoreCollectionData(companionsRef);

  const ticketReservationRef = gameRef
    .collection("ticketReservations")
    .doc(ticketReservation.userId);

  const { enqueueSnackbar } = useSnackbar();

  const {
    companionIds = [],
    companionIdsRedeemed = [],
    amountRedeemed = 0,
    userIdRedeemed = true,
    paymentStatus = "INCOMPLETE",
  } = ticketReservation;

  const [checkedCompanionIds, setCheckedCompanionIds] = React.useState(
    amountRedeemed > 0 ? companionIdsRedeemed : companionIds
  );

  const [isUserIdRedeemed, setIsUserIdRedeemed] = React.useState(
    userIdRedeemed
  );

  const handleToggle = (value: number) => () => {
    const currentIndex = checkedCompanionIds.indexOf(value);
    const newChecked = [...checkedCompanionIds];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedCompanionIds(newChecked);
  };

  const amountOfCards = isUserIdRedeemed
    ? checkedCompanionIds.length + 1
    : checkedCompanionIds.length;

  const { visitorCount = 0, visitorsWithReservationCount = 0 } = game;
  const newAmount = amountOfCards - amountRedeemed;

  return (
    <>
      <PageHeading mt={2}>Alle anwesend?</PageHeading>
      {amountRedeemed > 0 && (
        <Alert severity="warning">
          Du bearbeitest eine bereits eingelöste Reservierung. Wähle erneut alle
          Personen an, die in der Halle sind.
        </Alert>
      )}
      <Formik
        initialValues={{
          visitors: [],
        }}
        onSubmit={async () => {
          await gameRef
            .set(
              {
                visitorsWithReservationCount:
                  visitorsWithReservationCount + newAmount,
                visitorCount: visitorCount + newAmount,
              },
              { merge: true }
            )
            .catch((error) => {
              console.error(error);
              enqueueSnackbar(error.message || error, { variant: "error" });
            });

          ticketReservationRef
            .set(
              {
                amountRedeemed: amountOfCards,
                companionIdsRedeemed: checkedCompanionIds,
                userIdRedeemed: isUserIdRedeemed,
              },
              { merge: true }
            )
            .then(() => Promise.resolve())
            .catch((error) => {
              console.error(error);
              enqueueSnackbar(error.message || error, { variant: "error" });
            });

          enqueueSnackbar(`${amountOfCards} Besucher eingechecked`, {
            variant: "success",
            autoHideDuration: 2000,
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });

          if (keepOpen) {
            await setStep(1);
          } else {
            handleClose();
          }
        }}
      >
        {({ submitForm, isSubmitting }) => (
          <Form>
            <List>
              <ListItem
                button
                onClick={() => setIsUserIdRedeemed(!isUserIdRedeemed)}
              >
                <ListItemText
                  primary={userData.name}
                  secondary={`${userData.streetAddress}, ${
                    userData.postalCode
                  }, •••${userData.phoneNumber.substring(
                    userData.phoneNumber.length - 3
                  )}`}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    onChange={() => setIsUserIdRedeemed(!isUserIdRedeemed)}
                    checked={isUserIdRedeemed}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              {companionIds.length > 0 &&
                companionIds.map((companionId) => {
                  const companion = companions.find(
                    (c) => c.id === companionId
                  );
                  return (
                    <div key={companionId}>
                      <Divider />
                      <ListItem button onClick={handleToggle(companionId)}>
                        <ListItemText
                          primary={companion.name}
                          secondary={`${companion.streetAddress}, ${
                            companion.postalCode
                          }, •••${companion.phoneNumber.substring(
                            companion.phoneNumber.length - 3
                          )}`}
                        />
                        <ListItemSecondaryAction>
                          <Checkbox
                            edge="end"
                            onChange={handleToggle(companionId)}
                            checked={
                              checkedCompanionIds.indexOf(companionId) >= 0
                            }
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </div>
                  );
                })}
            </List>
            <Box mb={2}>
              {paymentStatus === "COMPLETE" ? (
                <Alert severity="success">
                  <b>Bereits bezahlt</b>
                </Alert>
              ) : (
                <Alert severity="info">
                  <b>{newAmount * 3},00 €</b> für {newAmount} Ticket
                  {newAmount === 1 ? "" : "s"} fällig
                </Alert>
              )}
            </Box>

            {isSubmitting ? (
              <LinearProgress mb={1} />
            ) : (
              <>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  color="primary"
                  onClick={submitForm}
                  disabled={amountOfCards < 1}
                >
                  Besucher einchecken
                </Button>
                <Box mt={4} style={{ textAlign: "center" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={keepOpen}
                        onChange={() => setKeepOpen(!keepOpen)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="textSecondary">
                        Check-In Seite geöffnet lassen
                      </Typography>
                    }
                  />
                </Box>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}

export default GameAdminSmartCheckInFormValidate;
